import { useCallback, useEffect, useState } from "react";

import { TextArea, TextField } from "@king-ict/eupisi-ui/components";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";

import {
  checkSchoolYearValidity,
  hasDecisionPermission,
} from "./CommitteeDecision";
import { DEFAULT_REQUEST } from "./constants";
import { formatDateTimeWithUser } from "./utils";
import { getCommitteeDecision } from "../../api/Request/RequestApi";
import {
  ContentArea,
  ToolbarTop,
  ToolbarEdit,
  FormCard,
  RequestHeader,
} from "../../components";
import { useAuth } from "../../hooks/auth/useAuth";
import {
  Request,
  CommitteeLanguageDecision,
  CommitteeEnrollmentDecision,
  CommitteeDecision,
} from "../../models/Request";
import { useGetSchoolYearCurrentQuery } from "../../queries/codeBookQuery";
import {
  committeeDecisionEditUrl,
  requestAdminDetailsUrl,
} from "../../routes/routes";
import { RuleUtils, MsgUtils } from "../../utils";
import { formatDate } from "../../utils/formatUtils";

export function CommitteeDecisionDetails() {
  const { committeeDecisionId, requestId, committeeEmployeeId } = useParams<{
    committeeDecisionId: string;
    requestId: string;
    committeeEmployeeId: string;
  }>();
  const { t } = useTranslation();
  const history = useHistory();
  const auth = useAuth();

  const [item, setItem] = useState<Request>(DEFAULT_REQUEST);
  const [croatianTeacher, setCroatianTeacher] = useState(false);
  const [committeeDecision, setCommitteeDecision] =
    useState<CommitteeDecision | null>(null);

  const { data: schoolYear } = useGetSchoolYearCurrentQuery();

  useEffect(() => {
    (async () => {
      if (committeeDecisionId && requestId && committeeEmployeeId) {
        var result = await getCommitteeDecision(
          +committeeDecisionId,
          +requestId,
          +committeeEmployeeId
        );
        if (result) {
          setCommitteeDecision(result);
          if (result.request) setItem(result.request);
          setCroatianTeacher(
            RuleUtils.isCroatianTeacherRole(
              result.committeeEmployee?.committeeRole?.id
            )
          );
        }
      }
    })();
  }, [committeeDecisionId, requestId, committeeEmployeeId]);

  const handleEditClicked = async () => {
    history.push(
      committeeDecisionEditUrl(
        +committeeDecisionId,
        +requestId,
        +committeeEmployeeId
      )
    );
  };
  const goBack = () => {
    history.push(requestAdminDetailsUrl(+requestId));
  };

  const showEditButtonInToolbar = useCallback(() => {
    const committeeType =
      committeeDecision?.committeeEmployee?.committeeRole?.committeeType;

    // Apply this rule to the committee decision details button
    if (!RuleUtils.canEditCommitteeDecision(item, committeeType)) {
      return false;
    }

    return (
      checkSchoolYearValidity(item.schoolYearId, schoolYear) &&
      hasDecisionPermission({
        committeeEmployee: committeeDecision?.committeeEmployee,
        auth,
        committeeType,
      })
    );
  }, [committeeDecision, item, schoolYear, auth]);

  return (
    <ContentArea>
      <ToolbarTop
        showBack={"show"}
        showEdit={showEditButtonInToolbar() ? "show" : "hide"}
        pageTitle={t("committeeDecision.committeeDecisionPageTitle")}
        onClickBack={goBack}
        onClickEdit={handleEditClicked}
      />
      <RequestHeader request={item} />
      <FormCard>
        <Row>
          <Col md={10}>
            <TextArea
              ariaLabel={t("committeeDecision.remark")}
              label={t("committeeDecision.remark")}
              value={committeeDecision?.remark}
              maxLength={1500}
              readonly
              disabled
              rows={10}
            />
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <div
              style={{ display: "flex", gap: "0.3rem", alignItems: "flex-end" }}
            >
              <TextField
                label={t("committeeDecision.committeeEmployee")}
                value={`${committeeDecision?.committeeEmployee?.employee?.name}, ${committeeDecision?.committeeEmployee?.committeeRole.name}`}
                plaintext
              />
              {!!committeeDecision?.committeeEmployee?.deactivationDate && (
                <TextField
                  value={`- aktivan do ${formatDate(
                    committeeDecision?.committeeEmployee?.deactivationDate
                  )}`}
                  plaintext
                  bold
                />
              )}
            </div>
          </Col>
          {committeeDecision?.committeeEmployee?.committeeId !==
            item.institutionCommitteeId && (
            <Col md={6}>
              <TextField
                label={t("committeeDecision.committeePosition")}
                value={
                  committeeDecision?.committeeEmployee?.committeePosition?.name
                }
                plaintext
              />
            </Col>
          )}
        </Row>
        <Row>
          <Col md={12}>
            <TextField
              label={t("committeeDecision.committeeEnrollmentDecision")}
              value={
                MsgUtils.getEnumToCodeBook(
                  CommitteeEnrollmentDecision,
                  "committeeEnrollmentDecision"
                ).find(
                  (v) =>
                    v.id === committeeDecision?.committeeEnrollmentDecisionId
                )?.name
              }
              plaintext
            />
          </Col>
        </Row>
        {croatianTeacher && (
          <Row>
            <Col md={12}>
              <TextField
                label={t("committeeDecision.committeeLanguageDecision")}
                value={
                  MsgUtils.getEnumToCodeBook(
                    CommitteeLanguageDecision,
                    "committeeLanguageDecision"
                  ).find(
                    (v) =>
                      v.id === committeeDecision?.committeeLanguageDecisionId
                  )?.name
                }
                plaintext
              />
            </Col>
          </Row>
        )}
        <Row>
          <Col md={12}>
            <TextField
              label={t("committeeDecision.createdDateTimeUser")}
              value={formatDateTimeWithUser(
                committeeDecision?.dateCreated,
                committeeDecision?.createdAppUser
              )}
              plaintext
            />
          </Col>
        </Row>
        {committeeDecision?.dateUpdated && (
          <Row>
            <Col md={12}>
              <TextField
                label={t("committeeDecision.lastChangedDateTimeUser")}
                value={formatDateTimeWithUser(
                  committeeDecision?.dateUpdated,
                  committeeDecision?.appUser
                )}
                plaintext
              />
            </Col>
          </Row>
        )}
      </FormCard>
      <ToolbarEdit
        showCancel={"show"}
        showSave={"hide"}
        onClickCancel={goBack}
      />
    </ContentArea>
  );
}
